import pako from 'pako';

// Utility to convert Base64 data back to binary string efficiently
const base64ToUint8Array = (base64) => {
//  console.log('[base64ToUint8Array] Converting Base64 to Uint8Array...');

  // NEW: normalize URL-safe base64 and fix missing padding
  let b64 = base64;

  if (typeof b64 !== 'string') {
    throw new Error(`[base64ToUint8Array] Expected base64 string, got ${typeof b64}`);
  }

  // Remove whitespace/newlines (copy/paste safety)
  b64 = b64.replace(/\s+/g, '');

  // URL-safe base64 → standard base64
  b64 = b64.replace(/-/g, '+').replace(/_/g, '/');

  // Add missing padding
  const pad = b64.length % 4;
  if (pad === 2) b64 += '==';
  else if (pad === 3) b64 += '=';
  else if (pad !== 0) {
    // pad === 1 is invalid base64 length
    throw new Error('[base64ToUint8Array] Invalid base64 length (mod 4 === 1)');
  }

  const binaryString = atob(b64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};


// Asynchronous utility to decompress data using pako
export const decompressData = async (compressedData) => {
  try {
//    console.log('[decompressData] Decompressing form data asynchronously...');

    // NEW: tolerate "already decompressed" values (common in your refactor)
    if (compressedData == null) {
      // null/undefined => nothing to decompress
      return null;
    }

    // If callers pass an object/array directly (already decompressed), return it
    if (typeof compressedData === 'object') {
      return compressedData;
    }

    // Only strings are candidates for base64+pako or raw JSON
    if (typeof compressedData !== 'string') {
//      console.warn('[decompressData] Non-string, non-object input; returning as-is:', typeof compressedData);
      return compressedData;
    }

    const s = compressedData.trim();

    // NEW: tolerate raw JSON strings (migration/debug paths)
    // If it looks like JSON, parse it directly.
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      return JSON.parse(s);
    }

    // Convert Base64 to Uint8Array for decompression
    const compressed = base64ToUint8Array(s);

    // Decompress the binary data back into a string using pako
    const decompressed = await pako.inflate(compressed, { to: 'string' });

//    console.log('[decompressData] Form data decompressed successfully.');

    // Parse JSON and return the decompressed data
    // NEW: if JSON parsing fails, surface a clearer error
    try {
      return JSON.parse(decompressed);
    } catch (jsonErr) {
      console.error('[decompressData] Decompressed payload was not valid JSON.', {
        message: jsonErr?.message,
        // keep small slice so logs don't explode
        preview: String(decompressed || '').slice(0, 200),
      });
      throw jsonErr;
    }
  } catch (error) {
    console.error('[decompressData] Error decompressing form data:', error);
    throw error; // Rethrow error to be handled by calling function
  }
};


// Optional: Chunking data decompression for very large forms
export const decompressChunks = async (compressedChunks) => {
  try {
//    console.log('[decompressChunks] Decompressing chunks of data...');

    if (!Array.isArray(compressedChunks)) {
      throw new Error('[decompressChunks] Expected an array of chunks.');
    }

    // Decompress each chunk asynchronously
    const decompressedChunks = await Promise.all(
      compressedChunks.map(async (chunk) => {
        // NEW: reuse shared logic (supports objects/null/JSON/base64)
        return await decompressData(chunk);
      })
    );

    // Combine the decompressed chunks into one object
    const combinedData = decompressedChunks.reduce((acc, chunk) => {
      // NEW: tolerate null chunks
      if (chunk == null) return acc;
      // If a chunk isn't an object, don't blow up; just attach it under a stable key
      if (typeof chunk !== 'object' || Array.isArray(chunk)) {
        return { ...acc, __chunk: chunk };
      }
      return { ...acc, ...chunk };
    }, {});

//    console.log('[decompressChunks] All chunks decompressed and combined successfully.');

    return combinedData;
  } catch (error) {
    console.error('[decompressChunks] Error decompressing chunks:', error);
    throw error; // Handle error if chunk decompression fails
  }
};
